import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, PenTool, CheckCircle, Clock, AlertCircle, 
  Download, Eye, GripVertical, User, FileCheck, DollarSign,
  ArrowRight, RefreshCw, X
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { signaturesAPI, poaAPI, referralAPI } from '../utils/api';

const SignatureWorkflow = ({ onStatsUpdate }) => {
  const [signers, setSigners] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [completedDocuments, setCompletedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedSigner, setDraggedSigner] = useState(null);
  const [dragOverDoc, setDragOverDoc] = useState(null);
  const [signingDoc, setSigningDoc] = useState(null);
  const [stats, setStats] = useState({
    pendingReview: 0,
    completed: 0,
    pendingReferrals: 0
  });

  const loadData = useCallback(async () => {
    try {
      const [signersRes, poaPendingRes, poaCompletedRes, referralsRes] = await Promise.all([
        signaturesAPI.getSigners().catch(() => ({ data: [] })),
        poaAPI.getPendingReviews().catch(() => ({ data: [] })),
        poaAPI.getAllPOA('completed').catch(() => ({ data: [] })),
        referralAPI.getMerchants().catch(() => ({ data: [] }))
      ]);

      const activeSigners = (signersRes.data || []).filter(s => s.is_active);
      setSigners(activeSigners);

      // Pending POAs
      const pendingPOAs = (poaPendingRes.data || []).map(doc => ({
        ...doc,
        type: 'poa',
        title: `Limited POA - ${doc.merchant_legal_name || doc.case_id?.substring(0, 8)}`,
        client_name: doc.signer_name,
        amount: doc.frozen_amount || 0,
        submitted_date: doc.client_signed_at || doc.created_at
      }));

      // Pending referrals that need signature
      const pendingReferrals = (referralsRes.data || [])
        .filter(r => r.status === 'pending' || r.status === 'accepted')
        .map(ref => ({
          ...ref,
          type: 'referral',
          title: `Referral Agreement - ${ref.merchant_company_name || ref.referrer_name || 'Unknown'}`,
          client_name: ref.merchant_contact_name || ref.merchant_signer_name || 'Unknown',
          amount: ref.held_reserves_amount || ref.total_held_reserves || 0,
          submitted_date: ref.submitted_date || ref.created_at,
          needs_signature: !ref.admin_signed
        }));

      // Combine all pending documents
      const allPending = [...pendingPOAs, ...pendingReferrals.filter(r => r.needs_signature)];
      setPendingDocuments(allPending);

      // Completed documents
      const completedPOAs = (poaCompletedRes.data || []).map(doc => ({
        ...doc,
        type: 'poa',
        title: `Limited POA - ${doc.merchant_legal_name || doc.case_id?.substring(0, 8)}`,
        client_name: doc.signer_name,
        amount: doc.frozen_amount || 0,
        completed_date: doc.admin_signed_at || doc.updated_at
      }));

      const completedReferrals = (referralsRes.data || [])
        .filter(r => r.admin_signed)
        .map(ref => ({
          ...ref,
          type: 'referral',
          title: `Referral Agreement - ${ref.merchant_company_name}`,
          client_name: ref.merchant_contact_name,
          amount: ref.held_reserves_amount || 0,
          completed_date: ref.admin_signed_date
        }));

      setCompletedDocuments([...completedPOAs, ...completedReferrals]);

      // Calculate stats
      const newStats = {
        pendingReview: allPending.length,
        completed: completedPOAs.length + completedReferrals.length,
        pendingReferrals: pendingReferrals.filter(r => r.needs_signature).length
      };
      setStats(newStats);
      if (onStatsUpdate) onStatsUpdate(newStats);

    } catch (error) {
      console.error('Failed to load signature data:', error);
    } finally {
      setLoading(false);
    }
  }, [onStatsUpdate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSignerDragStart = (e, signer) => {
    setDraggedSigner(signer);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', signer.id);
  };

  const handleSignerDragEnd = () => {
    setDraggedSigner(null);
    setDragOverDoc(null);
  };

  const handleDocDragOver = (e, docId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDoc(docId);
  };

  const handleDocDragLeave = () => {
    setDragOverDoc(null);
  };

  const handleDocDrop = async (e, doc) => {
    e.preventDefault();
    setDragOverDoc(null);

    if (!draggedSigner) return;

    setSigningDoc(doc.id);

    try {
      if (doc.type === 'poa') {
        // Sign POA document with correct field names
        await poaAPI.adminSignPOA({
          poa_id: doc.id,
          signer_name: draggedSigner.name,
          signer_title: draggedSigner.title,
          signature_text: draggedSigner.name,
          signature_font: 'cursive'
        });
        toast.success(`POA signed by ${draggedSigner.name}! Document moved to completed.`);
      } else if (doc.type === 'referral') {
        // Sign referral document
        await referralAPI.adminSignReferral(doc.id, {
          signer_name: draggedSigner.name,
          signer_title: draggedSigner.title,
          signature_text: draggedSigner.name
        });
        toast.success(`Referral agreement signed by ${draggedSigner.name}!`);
      }

      // Reload data to update counters and remove signed doc from pending
      await loadData();
    } catch (error) {
      console.error('Signing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSigningDoc(null);
      setDraggedSigner(null);
    }
  };

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const handleViewDocument = async (doc) => {
    try {
      let response;
      if (doc.type === 'poa') {
        response = await poaAPI.getPOAPdf(doc.id);
      } else if (doc.type === 'referral') {
        response = await referralAPI.getMerchantPdf(doc.id);
      }
      
      if (response) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPreviewUrl(url);
        setPreviewTitle(doc.title);
      }
    } catch (error) {
      console.error('Failed to view document:', error);
      toast.error('Failed to view document');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      let response;
      if (doc.type === 'poa') {
        response = await poaAPI.getPOAPdf(doc.id);
      } else if (doc.type === 'referral') {
        response = await referralAPI.getMerchantPdf(doc.id);
      }
      
      if (response) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.title.replace(/\s+/g, '_')}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('Document downloaded');
      }
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTitle('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-purple-600 mr-2" size={24} />
        <span className="text-slate-600">Loading signature workflow...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{stats.pendingReview}</p>
              <p className="text-sm text-amber-600">Pending Review</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <DollarSign className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{stats.pendingReferrals}</p>
              <p className="text-sm text-blue-600">Pending Referrals</p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
              <p className="text-sm text-green-600">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Workflow Area */}
      <div className="grid grid-cols-12 gap-6">
        {/* Authorized Signers Panel */}
        <div className="col-span-3 bg-slate-50 border border-slate-200 rounded-lg p-4">
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <User size={18} className="text-purple-600" />
            Authorized Signers
          </h3>
          <p className="text-xs text-slate-500 mb-4">Drag a signer to a document to sign</p>
          
          <div className="space-y-2">
            {signers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No active signers</p>
            ) : (
              signers.map((signer) => (
                <div
                  key={signer.id}
                  draggable
                  onDragStart={(e) => handleSignerDragStart(e, signer)}
                  onDragEnd={handleSignerDragEnd}
                  className={`p-3 bg-white border rounded-lg cursor-grab active:cursor-grabbing transition-all ${
                    draggedSigner?.id === signer.id 
                      ? 'border-purple-400 shadow-lg scale-105' 
                      : 'border-slate-200 hover:border-purple-300 hover:shadow'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="text-slate-400" size={16} />
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-bold text-purple-700">
                      {signer.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{signer.name}</p>
                      <p className="text-xs text-slate-500 truncate">{signer.title}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Documents Needing Signature */}
        <div className="col-span-9 space-y-6">
          {/* Pending Documents */}
          <div className="bg-white border border-slate-200 rounded-lg">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <AlertCircle size={18} className="text-amber-500" />
                Documents Awaiting Signature ({pendingDocuments.length})
              </h3>
              <button 
                onClick={loadData}
                className="text-slate-500 hover:text-slate-700 p-1"
                title="Refresh"
              >
                <RefreshCw size={16} />
              </button>
            </div>

            {pendingDocuments.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="mx-auto text-green-400 mb-3" size={48} />
                <p className="text-slate-600">All documents are signed!</p>
                <p className="text-sm text-slate-500">No pending signatures at this time.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {pendingDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    onDragOver={(e) => handleDocDragOver(e, doc.id)}
                    onDragLeave={handleDocDragLeave}
                    onDrop={(e) => handleDocDrop(e, doc)}
                    className={`p-4 transition-all ${
                      dragOverDoc === doc.id 
                        ? 'bg-purple-50 border-2 border-dashed border-purple-400' 
                        : signingDoc === doc.id
                        ? 'bg-green-50'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        doc.type === 'poa' ? 'bg-purple-100' : 'bg-cyan-100'
                      }`}>
                        {doc.type === 'poa' ? (
                          <FileCheck className="text-purple-600" size={20} />
                        ) : (
                          <DollarSign className="text-cyan-600" size={20} />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900 truncate">{doc.title}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            doc.type === 'poa' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-cyan-100 text-cyan-700'
                          }`}>
                            {doc.type === 'poa' ? 'POA' : 'Referral'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{doc.client_name}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-xs text-slate-500">
                            ${doc.amount?.toLocaleString() || '0'}
                          </span>
                          <span className="text-xs text-slate-500">
                            Submitted: {doc.submitted_date ? format(new Date(doc.submitted_date), 'MMM d, yyyy') : 'N/A'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {signingDoc === doc.id ? (
                          <div className="flex items-center gap-2 text-green-600">
                            <RefreshCw className="animate-spin" size={16} />
                            <span className="text-sm font-medium">Signing...</span>
                          </div>
                        ) : dragOverDoc === doc.id ? (
                          <div className="flex items-center gap-2 text-purple-600">
                            <PenTool size={16} />
                            <span className="text-sm font-medium">Drop to sign</span>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleViewDocument(doc)}
                              className="p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                              title="View"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDownloadDocument(doc)}
                              className="p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                              title="Download"
                            >
                              <Download size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {dragOverDoc === doc.id && (
                      <div className="mt-3 p-3 bg-purple-100 rounded-lg text-center">
                        <p className="text-sm font-medium text-purple-700">
                          Drop {draggedSigner?.name || 'signer'} here to sign this document
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Completed Documents */}
          <div className="bg-white border border-slate-200 rounded-lg">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-500" />
                Completed Documents ({completedDocuments.length})
              </h3>
            </div>

            {completedDocuments.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="mx-auto text-slate-300 mb-3" size={48} />
                <p className="text-slate-500">No completed documents yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
                {completedDocuments.slice(0, 10).map((doc) => (
                  <div key={doc.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        doc.type === 'poa' ? 'bg-green-100' : 'bg-green-100'
                      }`}>
                        <CheckCircle className="text-green-600" size={20} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 truncate">{doc.title}</h4>
                        <p className="text-sm text-slate-600">{doc.client_name}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-xs text-green-600 font-medium">
                            Completed: {doc.completed_date ? format(new Date(doc.completed_date), 'MMM d, yyyy') : 'N/A'}
                          </span>
                          <span className="text-xs text-slate-500">
                            ${doc.amount?.toLocaleString() || '0'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(doc)}
                          className="p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="Download"
                        >
                          <Download size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <h4 className="font-semibold text-slate-900 mb-2">How to Sign Documents</h4>
        <div className="flex items-center gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">1</span>
            <span>Drag an authorized signer from the left panel</span>
          </div>
          <ArrowRight className="text-slate-400" size={16} />
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">2</span>
            <span>Drop on a pending document to sign</span>
          </div>
          <ArrowRight className="text-slate-400" size={16} />
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">3</span>
            <span>Document moves to Completed</span>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">{previewTitle}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  download={`${previewTitle.replace(/\s+/g, '_')}.pdf`}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                >
                  <Download size={16} />
                  Download
                </a>
                <button
                  onClick={closePreview}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4 overflow-hidden">
              <object
                data={previewUrl}
                type="application/pdf"
                className="w-full h-full border border-slate-200 rounded-lg"
              >
                <div className="flex flex-col items-center justify-center h-full bg-slate-50 rounded-lg p-8">
                  <FileText className="text-slate-400 mb-4" size={64} />
                  <p className="text-slate-600 mb-4 text-center">
                    Your browser cannot display this PDF inline.
                  </p>
                  <div className="flex gap-3">
                    <a
                      href={previewUrl}
                      download={`${previewTitle.replace(/\s+/g, '_')}.pdf`}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Download size={18} />
                      Download PDF
                    </a>
                  </div>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureWorkflow;
